import styles from "./attaching-objects-to-task-modal.module.css";

import { useState, useEffect, useMemo, useRef } from "react";
import {
  Modal,
  Form,
  Typography,
  Button,
  Tag,
  Tooltip,
  InputNumber,
  Input,
  Flex,
} from "antd";

import PlusIcon from "@/icons/PlusIcon";
import NestedSourcesBlock from "./components/NestedCandidates";

import { getNestedCandidates, attachResources } from "@/api/tasks";
import { getItem as getDefinition } from "@/api/definitions";

const { Text } = Typography;

const mapObjectsToForm = (objects = []) => {
  return objects.map((obj) => {
    const grouped = {};

    (obj.nested_resources || []).forEach((res) => {
      const key = res.source_field_uuid;

      if (!grouped[key]) {
        grouped[key] = {
          field_uuid: key,
          resources: [],
        };
      }

      grouped[key].resources.push({
        object_uuid: res.object_uuid,
        quantity: res.quantity,
      });
    });

    return {
      object_uuid: obj.object_uuid,
      quantity: obj.quantity,
      sources: Object.values(grouped),
    };
  });
};

const mapObjectsToSave = (objects = []) => {
  return objects.map((obj) => {
    const nested_resources = [];

    (obj.sources || []).forEach((source) => {
      const source_field_uuid = source.field_uuid;

      (source.resources || []).forEach((res) => {
        if (!res.object_uuid) return;

        nested_resources.push({
          source_field_uuid,
          object_uuid: res.object_uuid,
          quantity: res.quantity,
        });
      });
    });

    return {
      object_uuid: obj.object_uuid,
      quantity: obj.quantity,
      nested_resources,
    };
  });
};

const SingleObjectNestedModal = ({
  value,
  task_uuid,
  visible,
  objectUuid,
  onSave,
  onCancel,
  width = 600,
  isAdmins = false,
}) => {
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [relatedOptionsMap, setRelatedOptionsMap] = useState({});
  const [tableOptions, setTableOptions] = useState([]);

  const fetchedKeysRef = useRef(new Set());

  const definitionUuid = value?.type?.definition?.uuid;

  const currentObject = useMemo(() => {
    return value?.objects?.find((o) => o.uuid === objectUuid);
  }, [value, objectUuid]);

  useEffect(() => {
    if (!currentObject) return;

    form.setFieldsValue({
      objects: mapObjectsToForm([currentObject]),
    });
  }, [currentObject, form]);

  const objects = Form.useWatch("objects", form);

  const valueNestedMap = useMemo(() => {
    if (!currentObject) return new Map();

    const map = new Map();

    (currentObject.nested_resources || []).forEach((res) => {
      const key = `${currentObject.object_uuid}_${res.source_field_uuid}`;

      if (!map.has(key)) {
        map.set(key, []);
      }

      map.get(key).push({
        value: res.object_uuid,
        label: res.name || res.object_uuid,
        searchLabel: res.name || res.object_uuid,
      });
    });

    return map;
  }, [currentObject]);

  useEffect(() => {
    const loadDefinition = async () => {
      if (!definitionUuid) return;

      try {
        const definition = await getDefinition(definitionUuid);

        const tables =
          definition?.fields?.self?.filter((f) => f.type === "table") || [];

        setTableOptions(
          tables.map((t) => ({
            value: t.uuid,
            label: t.name,
          })),
        );
      } catch (e) {
        console.error(e);
      }
    };

    loadDefinition();
  }, [definitionUuid]);

  useEffect(() => {
    const loadRelated = async () => {
      if (!objects?.length) return;

      const obj = objects[0];
      const parentObjectUuid = obj?.object_uuid;

      if (!parentObjectUuid) return;

      const requests = [];

      for (const source of obj?.sources || []) {
        const fieldUuid = source?.field_uuid;
        if (!fieldUuid) continue;

        const key = `${parentObjectUuid}_${fieldUuid}`;

        if (fetchedKeysRef.current.has(key)) continue;

        fetchedKeysRef.current.add(key);

        requests.push(
          getNestedCandidates(
            {
              task_uuid,
              parent_definition_uuid: definitionUuid,
              parent_object_uuid: parentObjectUuid,
              source_field_uuid: fieldUuid,
            },
            isAdmins,
          )
            .then((rows) => ({
              key,
              options: rows.map((row) => ({
                value: row.uuid,
                label: (
                  <div className={styles.objectOptionLabel}>
                    <span className={styles.objectOptionName}>{row.name}</span>

                    {row.unit?.name && (
                      <Tooltip title={row.unit?.name}>
                        <Tag className={styles.objectOptionUnitTag}>
                          {row.unit.name}
                        </Tag>
                      </Tooltip>
                    )}
                  </div>
                ),
                searchLabel: `${row.name} ${row.unit?.name ?? ""}`,
              })),
            }))
            .catch(() => null),
        );
      }

      if (!requests.length) return;

      const results = await Promise.all(requests);

      setRelatedOptionsMap((prev) => {
        const next = { ...prev };

        results.forEach((r) => {
          if (!r) return;

          const apiOptions = r.options || [];
          const valueOptions = valueNestedMap.get(r.key) || [];

          const map = new Map();

          valueOptions.forEach((opt) => {
            map.set(opt.value, opt);
          });

          apiOptions.forEach((opt) => {
            map.set(opt.value, opt);
          });

          next[r.key] = Array.from(map.values());
        });

        return next;
      });
    };

    loadRelated();
  }, [objects, task_uuid, definitionUuid, isAdmins, valueNestedMap]);

  const handleSave = async (values) => {
    setLoading(true);
    setError(null);

    try {
      const updated = values.objects[0];

      const original = mapObjectsToForm(value.objects);

      const merged = original.map((obj) =>
        obj.object_uuid === updated.object_uuid ? updated : obj,
      );

      const payload = {
        task_uuid,
        type: {
          ...value.type,
        },
        objects: mapObjectsToSave(merged),
      };

      await attachResources("PUT", payload);

      onSave();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={visible}
      title="Добавление связанных ресурсов"
      onCancel={onCancel}
      onOk={() => form.submit()}
      okButtonProps={{ loading }}
      width={width}
    >
      <Form form={form} layout="vertical" onFinish={handleSave}>
        <Form.Item name={["objects", 0, "object_uuid"]} hidden>
          <Input />
        </Form.Item>

        <Form.Item label="Ресурс" style={{ marginBottom: 10 }}>
          <Input value={currentObject?.name} disabled />
        </Form.Item>

        <Form.Item name={["objects", 0, "quantity"]} hidden>
          <InputNumber />
        </Form.Item>

        <Form.List name={["objects", 0, "sources"]}>
          {(fields, { add, remove }) => {
            const parentObjectUuid = form.getFieldValue([
              "objects",
              0,
              "object_uuid",
            ]);

            const canAdd = parentObjectUuid && tableOptions?.length;

            return tableOptions.length === 0 ? (
              <Flex vertical align="center">
                <span style={{ color: "#777" }}>
                  Отсутствуют источники для добавления связанных ресурсов
                </span>
              </Flex>
            ) : (
              <>
                {fields.map((sourceField) => (
                  <NestedSourcesBlock
                    key={sourceField.key}
                    field={{ name: 0 }}
                    sourceField={sourceField}
                    form={form}
                    tableOptions={tableOptions}
                    relatedOptionsMap={relatedOptionsMap}
                    removeSource={remove}
                  />
                ))}

                {canAdd && (
                  <Button
                    type="dashed"
                    icon={<PlusIcon />}
                    block
                    className={styles.addObjectButton}
                    onClick={() => add({ resources: [{ quantity: 1 }] })}
                  >
                    Cвязанный ресурс
                  </Button>
                )}
              </>
            );
          }}
        </Form.List>

        {error && <Text type="danger">{error}</Text>}
      </Form>
    </Modal>
  );
};

export default SingleObjectNestedModal;
